import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Button, FormGroup, Label, Input } from 'reactstrap';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { Autocomplete } from '@react-google-maps/api';
import { countryOptions } from 'data/countries';
import { setActiveFlow, setCurrentStep, setFormData } from 'store/slices/flowSlice';
import { useTranslation } from "react-i18next";


const LandForm = ({ onNext }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const fromCityZipRef = useRef(null);
  const toCityZipRef = useRef(null);

  // State to track if From and To locations are selected
  const [isFromSelected, setIsFromSelected] = useState(false);
  const [isToSelected, setIsToSelected] = useState(false);

  // Updated validation schema since we no longer need city/zipcode
  const SignupSchema = Yup.object().shape({
    toAddress: Yup.string().required(t('Required')),
    fromAddress: Yup.string().required(t('Required')),
    toLocation: Yup.object().required(t('Required')),
    fromLocation: Yup.object().required(t('Required')),
    deliveredToRecipient: false,
    pickupRequired: false
  });

  // Modified handlePlaceSelect to capture full address
  const handlePlaceSelect = (autocomplete, setFieldValue, field) => {
    if (autocomplete) {
      const place = autocomplete.getPlace();

      if (place && place.formatted_address) {
        sessionStorage.setItem(field, place?.formatted_address);
        setFieldValue(field, place.formatted_address);
      } else {
        alert('Unable to retrieve a valid address. Please try selecting again.');
      }
    } else {
      alert('Autocomplete is not initialized properly.');
    }
  };



  return (
    <Formik
      initialValues={{
        fromLocation: null,
        fromAddress: '',
        toLocation: null,
        toAddress: '',
      }}
      validationSchema={SignupSchema}
      onSubmit={(values) => {
        dispatch(setFormData(values));
        dispatch(setActiveFlow('land'));
        dispatch(setCurrentStep('1'));
        onNext();
      }}
    >
      {({ setFieldValue, errors, touched, values }) => (
        <Form>
          <Row>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                <Label className="e2g-cargo-form-label mb-2 fs-custom fw-bold text-dark e2g-form-size" for="fromLocation">
                  {t("From")}
                </Label>
                <Select
                  className="rounded border-0 e2g-form-input"
                  id="fromLocation"
                  options={countryOptions}
                  onChange={(option) => {
                    setFieldValue('fromLocation', option);
                    setIsFromSelected(!!option); // Set state based on selection
                  }}
                  placeholder={t("Select Location")}
                />
                {errors.fromLocation && touched.fromLocation ? (
                  <div className="e2g-error-text text-danger">{errors.fromLocation}</div>
                ) : null}
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                <Label className="e2g-cargo-form-label" for="fromAddress">
                  {t("From Address")}
                </Label>
                <Autocomplete
                  className="rounded border-0 e2g-form-input"
                  onLoad={(autocomplete) => (fromCityZipRef.current = autocomplete)}
                  key={'from'+values?.fromLocation?.value}
                  onPlaceChanged={() =>
                    handlePlaceSelect(fromCityZipRef.current, setFieldValue, 'fromAddress')
                  }
                  options={{
                    types: ['address'], // Restrict to address results
                    componentRestrictions: {
                      country: values.fromLocation ? values?.fromLocation?.value : '',
                    },
                  }}
                >
                  <Field
                    className="border-0 text-dark custom-input-field"
                    name="fromAddress"
                    as={Input}
                    placeholder={t("Provide Full Address")}
                    disabled={!isFromSelected} // Disable field if From location is not selected
                  />
                </Autocomplete>
                {errors.fromAddress && touched.fromAddress ? (
                  <div className="e2g-error-text text-danger">{errors.fromAddress}</div>
                ) : null}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                <Label className="e2g-cargo-form-label mb-2 fs-custom fw-bold text-dark e2g-form-size" for="toLocation">
                  {t("To")}
                </Label>
                <Select
                  className="rounded border-0 e2g-form-input"
                  id="toLocation"
                  options={countryOptions}
                  onChange={(option) => {
                    setFieldValue('toLocation', option);
                    setIsToSelected(!!option); // Set state based on selection
                  }}
                  placeholder={t("Select Location")}
                />
                {errors.toLocation && touched.toLocation ? (
                  <div className="e2g-error-text text-danger">{errors.toLocation}</div>
                ) : null}
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                <Label className="e2g-cargo-form-label" for="toAddress">
                  {t("To Address")}
                </Label>
                <Autocomplete
                  className="rounded border-0 e2g-form-input"
                  key={'to'+values?.toLocation?.value}
                  onLoad={(autocomplete) => (toCityZipRef.current = autocomplete)}
                  onPlaceChanged={() =>
                    handlePlaceSelect(toCityZipRef.current, setFieldValue, 'toAddress')
                  }
                  options={{
                    types: ['address'], // Restrict to address results
                    componentRestrictions: {
                      country: values.toLocation ? values?.toLocation?.value : '',
                    },
                  }}
                >
                  <Field
                    className="border-0 text-dark custom-input-field"
                    name="toAddress"
                    as={Input}
                    placeholder={t("Provide Full Address")}
                    disabled={!isToSelected} // Disable field if To location is not selected
                  />
                </Autocomplete>
                {errors.toAddress && touched.toAddress ? (
                  <div className="e2g-error-text text-danger">{errors.toAddress}</div>
                ) : null}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="12" className="text-left e2g-form-btn">
              <Button
                type="submit"
                color="dark"
                className="btn btn-dark rounded-pill e2g-land-btn text-capitalize fw-medium"
              >
                {t("Get a spot offer")}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default LandForm;